import React, { useMemo, useState } from "react";
import {
  Users, ShieldCheck, Lock, UserX, Activity, AlertTriangle, Users2, Building2, ShieldAlert,
  ChevronRight, Crown, Pencil, UserCog, RotateCcw, Target, DatabaseBackup, ScrollText,
  SlidersHorizontal, FileSearch, LifeBuoy, BookOpen, MessageSquare, Loader2, ListChecks,
} from "lucide-react";
import { Card } from "@/components/common";
import { TEAM_ROLE_CHIP, EVENT_LABEL, POLICY_DOT, POLICY_TEXT, deDT, teamRoleLabel, Pagination, CRITICAL_EVENTS } from "./shared";
import { AccountConfigCard } from "./AccountConfigCard";
import { StatCard, Avatar, avatarRing, RoleIcon, TwoFACell, accountStatus, IconBtn, eventIcon, countryFlag } from "./ui";

const fmt = (v) => (v == null ? "—" : v);

export function OverviewTab({ ctx }) {
  const { t, ov, log, nav, setTab, teamUsers, clients, busy, actions, matrix } = ctx;
  const st = ov?.stats || {};
  const today = ov?.today || {};
  const ca = ov?.client_access || {};
  const sa = ov?.security_actions || {};
  const sys = ov?.systemstatus || {};
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const pctTotal = (n) => (st.total ? `${(Math.round((n / st.total) * 1000) / 10)}% ${t("der Konten")}` : t("der Konten"));
  const trend = st.logins_trend;
  const teamTotal = (teamUsers || []).length;
  const cur = Math.min(page, Math.max(1, Math.ceil(teamTotal / perPage)));
  const teamPage = (teamUsers || []).slice((cur - 1) * perPage, cur * perPage);
  const scopeText = (u) => (u.role === "super_admin" || (u.client_scope || "alle") === "alle") ? t("Alle Kunden")
    : u.client_scope === "bestimmte" ? `${(u.scoped_client_ids || []).length} ${t("Kunden")}` : t("Keine Kunden");

  const evSeverity = (e) => {
    if (e.success === false) return e.event === "login_gesperrt" ? "red" : "amber";
    if (["rolle_geaendert", "team_rolle_geaendert", "kunden_zuweisung_geaendert"].includes(e.event)) return "blue";
    if ((e.event || "").includes("2fa")) return "amber";
    return "green";
  };
  const SEV_DOT = { green: "bg-[#A3E635]", amber: "bg-amber-400", red: "bg-red-500", blue: "bg-sky-400" };

  if (!ov) return <div className="py-16 text-center" data-testid="sec-panel-overview"><Loader2 className="w-6 h-6 animate-spin text-[#A3E635] mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="sec-panel-overview">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="sec-stat-grid">
        <StatCard icon={Users} testid="stat-total" label={t("Konten gesamt")} value={fmt(st.total)} sub={`${(ov.roles || {}).super_admin || 1} ${t("Super Admin")}`} accent="lime" />
        <StatCard icon={ShieldCheck} testid="stat-2fa" label={t("2FA aktiv")} value={fmt(st.twofa)} sub={`${st.twofa_pct ?? 0}% ${t("der Konten")}`} accent={st.twofa > 0 ? "lime" : "amber"} />
        <StatCard icon={Lock} testid="stat-locked" label={t("Gesperrte Konten")} value={st.locked ?? 0} sub={pctTotal(st.locked || 0)} accent={st.locked > 0 ? "red" : "gray"} />
        <StatCard icon={UserX} testid="stat-inactive" label={t("Inaktive Konten")} value={st.inactive ?? 0} sub={pctTotal(st.inactive || 0)} accent={st.inactive > 0 ? "amber" : "gray"} />
        <StatCard icon={Activity} testid="stat-logins" label={t("Logins (24 h)")} value={fmt(st.logins_24h)} sub={trend == null ? t("Keine Vergleichsdaten") : `${trend >= 0 ? "+" : ""}${trend}% ${t("vs. gestern")}`} accent="sky" />
        <StatCard icon={AlertTriangle} testid="stat-warnings" label={t("Sicherheitswarnungen")} value={st.warnings ?? 0} sub={st.warnings > 0 ? t("Aktion erforderlich") : t("Alles in Ordnung")} accent={st.warnings > 0 ? "amber" : "lime"} />
      </div>

      {/* 3 panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="sec-mgmt-cards">
        <Panel icon={ListChecks} title={t("Heute wichtig")} arrow onArrow={() => nav("/admin/tasks")} testid="panel-today"
          rows={[
            [t("Offene Einladungen"), today.open_invites, today.open_invites > 0 ? "amber" : "gray", "today-invites"],
            [t("Teammitglieder ohne 2FA"), today.team_no_2fa, today.team_no_2fa > 0 ? "amber" : "lime", "today-no2fa"],
            [t("Ohne Kundenzuweisung"), today.no_client_assignment, today.no_client_assignment > 0 ? "amber" : "gray", "today-noscope"],
            [t("Ablaufende Passwörter (7 Tage)"), today.expiring_pw, today.expiring_pw > 0 ? "red" : "gray", "today-pwexp"],
          ]}
          btn={{ icon: ListChecks, label: t("Alle Aufgaben anzeigen"), onClick: () => nav("/admin/tasks"), testid: "today-btn" }} />

        <Panel icon={Building2} title={t("Kundenbenutzer & Zugänge")} testid="panel-clients"
          rows={[
            [t("Kunden-Admins"), ca.client_admins, "default", "ca-admins"],
            [t("Kunden-Mitarbeiter"), ca.client_staff, "default", "ca-staff"],
            [t("Letzter Kunden-Login"), deDT(ca.last_client_login) || t("Noch nie"), ca.last_client_login ? "default" : "gray", "ca-last"],
            [t("Aktive Kunden-Zugänge"), ca.active_client_access, "lime", "ca-active"],
            [t("Offene Zuweisungen"), ca.open_assignments, ca.open_assignments > 0 ? "amber" : "lime", "ca-open"],
          ]}
          btn={{ icon: Target, label: t("Kunden-Zuweisungen verwalten"), onClick: () => setTab("assign"), testid: "ca-btn" }} />

        <Panel icon={ShieldAlert} title={t("Sicherheits-Aktionen")} testid="panel-security"
          rows={[
            [t("2FA bei Admins offen"), sa.admin_2fa_open, sa.admin_2fa_open > 0 ? "amber" : "lime", "sa-2fa"],
            [t("Verdächtige Logins (24 h)"), sa.suspicious_24h, sa.suspicious_24h > 0 ? "amber" : "lime", "sa-susp"],
            [t("Gesperrte Konten"), sa.locked, sa.locked > 0 ? "red" : "gray", "sa-locked"],
            [t("Kritische Richtlinienverletzungen"), sa.critical_violations, sa.critical_violations > 0 ? "red" : "lime", "sa-viol"],
            [t("Letzte kritische Aktion"), deDT(sa.last_critical_action) || t("Keine"), sa.last_critical_action ? "default" : "gray", "sa-last"],
          ]}
          btn={{ icon: DatabaseBackup, label: t("Sicherheitscenter öffnen"), onClick: () => nav("/admin/qa"), testid: "sa-btn" }} />
      </div>

      {/* Team + Roles/Policies */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        <Card className="p-0 overflow-hidden xl:col-span-8" data-testid="sec-overview-team">
          <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2">
            <div className="font-bold tracking-tight text-sm flex items-center gap-2"><Users2 className="w-4 h-4 text-[#A3E635]" /> {t("KEWAG-Team")} <span className="text-gray-500 font-normal">{teamTotal}</span></div>
            <button onClick={() => setTab("team")} data-testid="overview-team-all" className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1">{t("Alle Teammitglieder anzeigen")} <ChevronRight className="w-3 h-3" /></button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                <th className="text-left font-semibold px-5 py-2.5">{t("Name")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{t("Rolle")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">{t("Kunden-Zugriff")}</th>
                <th className="text-center font-semibold px-3 py-2.5">2FA</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">{t("Letzter Login")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{t("Status")}</th>
                <th className="text-right font-semibold px-5 py-2.5">{t("Aktion")}</th>
              </tr></thead>
              <tbody>
                {teamPage.map((u) => { const s = accountStatus(u, t); const owner = u.role === "super_admin"; return (
                  <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]" data-testid={`overview-team-row-${u.id}`}>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={u.name} accent={avatarRing(u)} size="sm" />
                        <div className="min-w-0">
                          <div className="font-semibold truncate max-w-[160px] flex items-center gap-1.5">{owner && <Crown className="w-3.5 h-3.5 text-[#A3E635] shrink-0" />}{u.name}</div>
                          <div className="text-[11px] text-gray-500 truncate max-w-[160px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TEAM_ROLE_CHIP[u.team_role] || "bg-white/5 border-white/10 text-gray-400"}`}>{teamRoleLabel(matrix, u.team_role)}</span></td>
                    <td className="px-3 py-2.5 text-xs text-gray-300 hidden lg:table-cell">{scopeText(u)}</td>
                    <td className="px-3 py-2.5 text-center"><div className="flex justify-center"><TwoFACell on={u.totp_enabled} t={t} /></div></td>
                    <td className="px-3 py-2.5 text-gray-400 text-xs whitespace-nowrap hidden md:table-cell">{deDT(u.last_login) || t("Noch nie")}</td>
                    <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${s.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span></td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-0.5">
                        {busy === u.id ? <RotateCcw className="w-4 h-4 animate-spin text-gray-400" /> : <>
                          <IconBtn icon={Pencil} label={t("Bearbeiten")} onClick={() => actions.edit(u)} testid={`ov-edit-${u.id}`} />
                          {!owner && <IconBtn icon={Target} label={t("Kunden zuweisen")} onClick={() => actions.scope(u)} testid={`ov-scope-${u.id}`} />}
                          <IconBtn icon={RotateCcw} label={t("2FA zurücksetzen")} onClick={() => actions.reset2fa(u)} testid={`ov-2fa-${u.id}`} />
                          {!owner ? <IconBtn icon={UserCog} label={t("Team-Rolle ändern")} onClick={() => actions.teamRole(u)} testid={`ov-teamrole-${u.id}`} />
                            : <span className="inline-flex items-center gap-1 text-[10px] text-[#A3E635] font-semibold pl-1"><Crown className="w-3 h-3" /> {t("Geschützt")}</span>}
                        </>}
                      </div>
                    </td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
          {teamTotal > 0 && <Pagination page={cur} setPage={setPage} total={teamTotal} perPage={perPage} setPerPage={setPerPage} t={t} testid="overview-team-pagination" />}
        </Card>

        <div className="xl:col-span-4 space-y-6">
          <Card className="p-5" data-testid="sec-overview-roles">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="font-bold tracking-tight text-sm flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-[#A3E635]" /> {t("Rollen & Rechte")}</div>
              <button onClick={() => setTab("roles")} data-testid="overview-roles-all" className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1">{t("Alle anzeigen")} <ChevronRight className="w-3 h-3" /></button>
            </div>
            <div className="space-y-0">
              {(ov.roles_detail || []).map((r) => (
                <div key={r.key} className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0" data-testid={`overview-role-${r.key}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <RoleIcon roleKey={r.key} />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate flex items-center gap-1.5">{r.label}{r.super_only && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-bold uppercase tracking-wide">{t("Kritisch")}</span>}</div>
                      <div className="text-[10px] text-gray-500 truncate max-w-[180px]">{r.description}</div>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-gray-300 shrink-0">{r.count}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setTab("roles")} data-testid="overview-roles-manage" className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-100 text-sm font-semibold py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors"><SlidersHorizontal className="w-4 h-4 text-[#A3E635]" /> {t("Alle Rollen verwalten")} <ChevronRight className="w-3.5 h-3.5 text-gray-500" /></button>
          </Card>

          <Card className="p-5" data-testid="sec-policies-card">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400"><ShieldCheck className="w-4 h-4 text-[#A3E635]" /> {t("Sicherheitsrichtlinien")}</div>
              <button onClick={() => nav("/admin/qa")} data-testid="policies-all" className="text-[11px] text-gray-400 hover:text-white">{t("Alle anzeigen")}</button>
            </div>
            <div className="space-y-3">
              {(ov.policies || []).map((p) => (
                <div key={p.key} className="flex items-start gap-2.5" data-testid={`policy-${p.key}`}>
                  <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${POLICY_DOT[p.color] || POLICY_DOT.gray}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{t(p.label)}</div>
                    <div className={`text-[11px] ${POLICY_TEXT[p.color] || "text-gray-400"}`}>{p.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Events + System/Help */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
        <Card className="p-0 overflow-hidden xl:col-span-8" data-testid="sec-overview-events">
          <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold tracking-tight"><FileSearch className="w-4 h-4 text-[#A3E635]" /> {t("Letzte Sicherheitsereignisse")}</div>
            <button onClick={() => setTab("audit")} data-testid="overview-audit-open" className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1">{t("Alle Ereignisse anzeigen")} <ChevronRight className="w-3 h-3" /></button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                <th className="text-left font-semibold px-5 py-2.5">{t("Zeitpunkt")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{t("Ereignis")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">{t("Benutzer")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">{t("Details")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">{t("IP-Adresse")}</th>
                <th className="text-left font-semibold px-5 py-2.5 hidden lg:table-cell">{t("Ort")}</th>
              </tr></thead>
              <tbody>
                {!log && <tr><td colSpan={6} className="px-5 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></td></tr>}
                {log && log.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-gray-500 text-sm">{t("Keine Ereignisse vorhanden.")}</td></tr>}
                {(log || []).slice(0, 7).map((e) => { const sev = evSeverity(e); const Icon = eventIcon(e); return (
                  <tr key={e.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]" data-testid={`overview-event-${e.id}`}>
                    <td className="px-5 py-2.5"><span className="inline-flex items-center gap-2 text-xs text-gray-400 whitespace-nowrap"><span className={`w-2 h-2 rounded-full shrink-0 ${SEV_DOT[sev]}`} />{deDT(e.created_at)}</span></td>
                    <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1.5 text-sm"><Icon className="w-3.5 h-3.5 text-gray-400" /> {t(EVENT_LABEL[e.event] || "Sicherheitsereignis")}</span></td>
                    <td className="px-3 py-2.5 hidden md:table-cell"><div className="text-[11px] text-gray-300 truncate max-w-[150px]">{e.email || "—"}</div></td>
                    <td className="px-3 py-2.5 text-[11px] text-gray-500 truncate max-w-[180px] hidden lg:table-cell">{e.detail || "—"}</td>
                    <td className="px-3 py-2.5 text-[11px] text-gray-500 font-mono hidden md:table-cell">{e.ip || "—"}</td>
                    <td className="px-5 py-2.5 hidden lg:table-cell">{e.location ? <span className="text-[11px] text-gray-300 inline-flex items-center gap-1.5"><span>{countryFlag(e.country)}</span>{e.location}</span> : <span className="text-[11px] text-gray-600">{t("Nicht verfügbar")}</span>}</td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="xl:col-span-4 space-y-6">
          <Card className="p-5" data-testid="sec-help-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-4"><LifeBuoy className="w-4 h-4 text-[#A3E635]" /> {t("System & Hilfe")}</div>
            <div className="space-y-1">
              <HelpRow icon={DatabaseBackup} label={t("Sicherheitscenter")} sub={t("Erweiterte Sicherheitsoptionen")} onClick={() => nav("/admin/qa")} testid="help-security-center" />
              <HelpRow icon={BookOpen} label={t("Hilfe & Dokumentation")} sub={t("Anleitungen & Best Practices")} onClick={() => nav("/admin/qa")} testid="help-docs" />
              <HelpRow icon={MessageSquare} label={t("Support kontaktieren")} sub={t("Unser Team ist für Sie da")} onClick={() => nav("/admin/support")} testid="help-support" />
            </div>
          </Card>

          {ctx.me?.role === "super_admin" && <AccountConfigCard t={t} />}

          <Card className="p-5" data-testid="sec-systemstatus-card">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3"><Activity className="w-4 h-4 text-[#A3E635]" /> {t("Benutzer & Sicherheit")}</div>
            <p className="text-sm text-gray-400">{t("Konten gesamt")}: {sys.accounts_total ?? st.total ?? 0}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Panel({ icon: Icon, title, rows, btn, arrow, onArrow, testid }) {
  const TONE = { lime: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", gray: "text-gray-500", default: "text-white" };
  return (
    <Card className="p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">{Icon && <Icon className="w-4 h-4 text-[#A3E635]" />} {title}</div>
        {arrow && <button onClick={onArrow} className="text-gray-500 hover:text-white"><ChevronRight className="w-4 h-4" /></button>}
      </div>
      <div className="space-y-0">
        {rows.map(([label, value, tone, tid]) => (
          <div key={tid} className="flex items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0" data-testid={tid}>
            <span className="text-xs text-gray-400">{label}</span>
            <span className={`text-sm font-bold ${TONE[tone] || TONE.default}`}>{value == null ? "—" : value}</span>
          </div>
        ))}
      </div>
      <button onClick={btn.onClick} data-testid={btn.testid} className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-100 text-sm font-semibold py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
        {btn.icon && <btn.icon className="w-4 h-4 text-[#A3E635]" />} {btn.label} <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
      </button>
    </Card>
  );
}

function HelpRow({ icon: Icon, label, sub, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors text-left">
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#A3E635]" /></div>
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{label}</div>
        <div className="text-[11px] text-gray-500 truncate">{sub}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-600 ml-auto shrink-0" />
    </button>
  );
}
